import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';

import { EventScoreboard, ScoreboardType, TimerType, TournamentScore } from '@frontend/sports/betting-offer/feature/model';
import { SitecoreEventDetailsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { AmericanFootballPeriodInfo, BasketballPeriodInfo, SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TickerInterval, TickerService } from '@frontend/sports/common/core/utils/timer';
import { EventDetailsModel } from '@frontend/sports/types/configuration/client-configuration';
import { PLATFORM } from '@frontend/vanilla/ssr';
import { ceil, padStart } from 'lodash-es';
import { Subscription, firstValueFrom } from 'rxjs';

@Component({
    selector: 'ms-live-timer',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveTimerComponent implements OnDestroy, OnChanges, OnInit {
    private sitecoreEventDetails = inject(SitecoreEventDetailsConfig);
    private ticker = inject(TickerService);
    private elementRef = inject(ElementRef);
    private sitecoreMyBets = inject(SitecoreMyBetsConfig);

    @Input() scoreboard: EventScoreboard;
    @Input() timeValue?: string;
    @Input() eventId?: number;
    @Input() scoreboardType: string;

    private gameTime: number; // seconds
    private serverTime: string;
    private tickerFormattedTime = '';
    private startTime = 0;

    private tickerSubscription: Subscription;
    private formatterFunc: () => string;

    private platform = inject(PLATFORM);

    async ngOnInit(): Promise<void> {
        await firstValueFrom(this.sitecoreEventDetails.whenReady);
    }

    ngOnChanges(changes: ISimpleChanges<LiveTimerComponent>): void {
        if (!changes.scoreboard) {
            return;
        }

        const scoreboardTypeChanged =
            changes.scoreboard.previousValue && changes.scoreboard.previousValue.type !== changes.scoreboard.currentValue.type;

        if (changes.scoreboard.isFirstChange() || scoreboardTypeChanged) {
            this.formatterFunc = this.getTimeFormatter();
        }

        this.restartTimer();
    }

    ngOnDestroy(): void {
        this.stopTicker();
    }

    private getTimeFormatter(): () => string {
        if (this.isType(ScoreboardType.TournamentGame)) {
            return this.getTournamentTime;
        }

        if (this.isType(ScoreboardType.PairGame, ScoreboardType.PeriodGame, ScoreboardType.SpecialGame, ScoreboardType.LeaderboardGame)) {
            return this.getPairGameTime;
        }

        if (this.isType(ScoreboardType.Darts, ScoreboardType.SetGame, ScoreboardType.BaseballGame, ScoreboardType.Cricket)) {
            return this.getOtherScoreboardTypesTime;
        }

        return () => '';
    }

    private restartTimer(): void {
        if (!this.scoreboard.started) {
            return;
        }

        this.gameTime = Number(this.scoreboard.gameTimeInSeconds);
        this.serverTime = this.scoreboard.timeIndicator || '';

        this.startTicker();
    }

    private startTicker(): void {
        this.stopTicker();

        if (this.scoreboard.timer && this.scoreboard.timer.base && this.scoreboard.timer.running) {
            this.gameTime = this.getTime();
            this.tickerFormattedTime = this.formatTime(this.gameTime);
            this.startCounter();
        }

        this.ticked();
    }

    private startCounter(): void {
        this.stopTicker();

        this.startTime = this.now();

        if (this.platform.isBrowser) {
            this.tickerSubscription = this.ticker.subscribe(() => this.ticked(), TickerInterval.Second);
        }
    }

    private ticked(): void {
        if (this.scoreboard.timer) {
            this.updateTickerFormattedTime();
        }

        this.elementRef.nativeElement.textContent = this.formatterFunc();
    }

    private now(): number {
        return ceil(DateProviderService.now(), -3);
    }

    private updateTickerFormattedTime(): void {
        const currentTime = this.now();

        const elapsedTime = Math.floor((currentTime - this.startTime) / 1000);

        this.tickerFormattedTime =
            this.scoreboard.timerType === TimerType.CountDown
                ? this.formatTime(Math.max(0, this.gameTime - elapsedTime))
                : this.formatTime(this.gameTime + elapsedTime);
    }

    private getOtherScoreboardTypesTime = (): string => {
        if (this.scoreboardType === 'large' && this.scoreboard.period?.id === BasketballPeriodInfo.Finished) {
            return this.sitecoreMyBets?.myBets?.Final || '';
        }
        return (this.scoreboard.started && this.scoreboard.period?.name) || '';
    };

    private getTournamentTime = (): string => {
        if (!this.scoreboard.visible) {
            return '';
        }

        const score = this.scoreboard.getScore<TournamentScore>();
        if (!score || Object.keys(score).length === 0) {
            return '';
        }

        //TODO any index issue, probably a defect, score.type key is km, but event details key is Kilometer
        const type = this.sitecoreEventDetails.eventDetails[score.type as keyof EventDetailsModel] || '';

        let stateLabel = score.current ? ` ${score.current}` : '';
        stateLabel += score.final ? `/${score.final}` : '';

        if (type) {
            stateLabel = type + stateLabel;
        } else {
            stateLabel += score.type || '';
        }
        const isTimerVisible = this.scoreboard.timerVisible && !this.scoreboard.isSuspended;

        if (isTimerVisible && this.scoreboard.timerRunning && this.eventId !== SportConstant.Golf) {
            stateLabel += ` • ${this.tickerFormattedTime}`;
        }

        return stateLabel;
    };

    private getPairGameTime = (): string => {
        let period = this.scoreboard.period!.name;
        const isTimerVisible = this.scoreboard.timerVisible && !this.scoreboard.isSuspended;

        const isGameFinished = this.scoreboard.period?.id === AmericanFootballPeriodInfo.Finished;
        const isScoreboardLarge = this.scoreboardType === 'large';
        this.timeValue =
            this.timeValue !== undefined &&
            (this.scoreboard.period?.id === AmericanFootballPeriodInfo.IntermissionQ1 ||
                this.scoreboard.period?.id === AmericanFootballPeriodInfo.IntermissionQ2 ||
                this.scoreboard.period?.id === AmericanFootballPeriodInfo.IntermissionQ3 ||
                this.scoreboard.period?.id === AmericanFootballPeriodInfo.RegularTimeOver ||
                this.scoreboard.period?.id === AmericanFootballPeriodInfo.Finished)
                ? ''
                : this.timeValue;
        if (isGameFinished && isScoreboardLarge) {
            period = this.sitecoreMyBets.myBets.Final ?? '';
        } else if (this.timeValue) {
            period += ` ${this.timeValue}`;
        } else {
            if (isTimerVisible) {
                period += this.scoreboard.timerRunning ? ` • ${this.tickerFormattedTime}` : ` ${this.serverTime.replace(period, '')}`;
            }
        }
        return period;
    };

    private getTime(): number {
        if (!this.scoreboard.timer) {
            return Number(this.scoreboard.gameTimeInSeconds);
        }
        let timerSeconds = this.scoreboard.timer.seconds;
        if (timerSeconds <= 0) {
            timerSeconds = this.getTotalSeconds(this.scoreboard.timer.value);
        }

        const base = new Date(this.scoreboard.timer.base).getTime();
        const now = this.now();

        //Countdown timers need the adjust the time down, countup - up.
        const time =
            this.scoreboard.timerType === TimerType.CountDown
                ? timerSeconds - Math.round((now - base) / 1000)
                : timerSeconds + Math.round((now - base) / 1000);

        return Math.max(time, 0);
    }

    private getTotalSeconds(timer: string): number {
        const values = timer?.split(':');
        switch (values?.length) {
            case 1:
                return parseInt(values[0], 10);
            case 2:
                return parseInt(values[0], 10) * 60 + parseInt(values[1], 10);
            case 3:
                return parseInt(values[0], 10) * 3600 + parseInt(values[1], 10) * 60 + parseInt(values[2], 10);
        }

        return 0;
    }

    private formatTime(timeSpan: number): string {
        const minutes = Math.floor(timeSpan / 60);
        const seconds = timeSpan % 60;

        return `${this.pad(minutes)}:${this.pad(seconds)}`;
    }

    private pad(val: number): string {
        return padStart(val.toString(), 2, '0');
    }

    private isType(...types: ScoreboardType[]): boolean {
        return types.includes(this.scoreboard.type);
    }

    private stopTicker(): void {
        this.tickerSubscription?.unsubscribe();
    }
}
